"use client";

import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import {
  PENDING_START_QUERY_PARAM,
  savePendingDriftChallenge,
} from "@/lib/pending-session";

const BETS = [
  "Write a 200-word positioning statement using this angle and send it to one honest person.",
  "Find one product that succeeded by targeting first-timers and map what they did differently.",
  "Try this framing in one real piece of copy — a headline, a bio, one paragraph.",
];

const PLACEHOLDER_PROMPTS = [
  "I'm launching a new product but every positioning angle feels like everyone else's...",
  "My team keeps having the same meeting about the same problem and nothing changes...",
  "I need a big idea for a client pitch and everything I come up with feels safe...",
  "Something about our strategy feels off but I can't name what it is yet...",
];

interface DemoSmallBetsProps {
  isLoggedIn: boolean;
}

export default function DemoSmallBets({ isLoggedIn }: DemoSmallBetsProps) {
  const router = useRouter();
  const [challengeInput, setChallengeInput] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [betsVisible, setBetsVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggered = useRef(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setPlaceholderIndex((prev) => (prev + 1) % PLACEHOLDER_PROMPTS.length);
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !triggered.current) {
          triggered.current = true;
          setTimeout(() => setBetsVisible(true), 400);
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const wordCount = challengeInput.trim().split(/\s+/).filter(Boolean).length;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const challenge = challengeInput.trim();
    if (!challenge || isSubmitting) return;

    savePendingDriftChallenge(challenge);
    setIsSubmitting(true);

    const sessionTarget = `/app/session-v2?${PENDING_START_QUERY_PARAM}=1`;
    if (isLoggedIn) {
      router.push(sessionTarget);
    } else {
      router.push(`/auth/sign-in?redirect=${encodeURIComponent(sessionTarget)}`);
    }
  };

  return (
    <div ref={containerRef} className="w-full max-w-lg scrolly-reveal">
      {/* The idea card being expanded */}
      <div className="border border-black/10 bg-white mb-0">
        <div className="px-5 py-4">
          <div className="flex gap-3 items-start">
            <span className="text-[10px] text-gray-300 tracking-[0.18em] pt-0.5 shrink-0 tabular-nums font-bold stack uppercase leading-none">
              IDEA{" "}
              <span className="type-display text-[1.35rem] font-black text-black/50 leading-none tracking-normal">02</span>
            </span>
            <div className="flex-1 stack gap-1.5">
              <p className="text-[10px] font-bold uppercase tracking-widest" >
                SNEAK LAUNCH
              </p>
              <p className="text-sm text-gray-900 leading-normal">
                What if the launch happened in a channel nobody in your category uses — leading to organic discovery before competitors can react?
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Expanding bets panel */}
      <div
        className="border border-t-0 border-black/10 bg-white overflow-hidden transition-all duration-700"
        style={{ maxHeight: betsVisible ? "1000px" : "0px", opacity: betsVisible ? 1 : 0 }}
      >
        <div className="px-5 pt-4 pb-2">
          <p className="text-[10px] uppercase tracking-[0.25em] text-gray-400 font-semibold mb-3">
            Try this
          </p>
          <div className="stack gap-3">
            {BETS.map((bet, index) => (
              <div
                key={index}
                className="flex gap-3 items-start border border-black/10 bg-white p-4"
                style={{
                  transition: `opacity 0.5s ease ${index * 0.15 + 0.2}s, transform 0.5s ease ${index * 0.15 + 0.2}s`,
                  opacity: betsVisible ? 1 : 0,
                  transform: betsVisible ? "translateY(0)" : "translateY(12px)",
                }}
              >
                <span className="text-[10px] text-gray-300 tracking-[0.2em] shrink-0 tabular-nums font-semibold pt-0.5">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <p className="text-sm text-gray-800 leading-relaxed">{bet}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Divider + CTA Form */}
        {/* <div className="px-5 pt-4 pb-5 mt-2 border-t border-black/10 stack gap-3">
          <p className="text-[10px] uppercase tracking-[0.25em] text-gray-400 font-semibold">
            Now it&rsquo;s your turn
          </p>
          <form onSubmit={handleSubmit} className="stack gap-3">
            <div className="border border-black/10 bg-white">
              <div className="flex items-center justify-between border-b border-black/10 px-4 py-2.5">
                <p className="text-[10px] uppercase tracking-[0.25em] text-gray-400 font-semibold">
                  Your Challenge
                </p>
                <p className="text-[10px] text-gray-400 tabular-nums">
                  {wordCount} {wordCount === 1 ? "word" : "words"}
                </p>
              </div>
              <textarea
                value={challengeInput}
                onChange={(e) => setChallengeInput(e.target.value)}
                placeholder={PLACEHOLDER_PROMPTS[placeholderIndex]}
                className="w-full min-h-[90px] px-4 py-3 text-sm border-0 focus:outline-none resize-none bg-transparent"
                disabled={isSubmitting}
              />
            </div>
            <button
              type="submit"
              disabled={!challengeInput.trim() || isSubmitting}
              className="w-full border border-black bg-black text-white px-6 py-3 text-[11px] font-bold uppercase tracking-[0.2em] hover:bg-white hover:text-black transition-colors disabled:opacity-40"
            >
              {isSubmitting ? "Starting..." : "Let's Thinknado →"}
            </button>
            {!isLoggedIn && (
              <p className="text-[10px] text-gray-400 text-center">
                Next: sign in or create your account — then your session starts automatically.
              </p>
            )}
          </form>
        </div> */}
      </div>
    </div>
  );
}
